/**
 * Client-side image compression.
 * Downscales to fit within `maxDimension` px and re-encodes as JPEG, lowering
 * quality progressively until the encoded size is <= `maxBytes` (default 3MB).
 *
 * Returns { dataUrl, bytes, width, height, mime }.
 */
export async function compressImage(file, {
  maxBytes = 3 * 1024 * 1024,   // 3 MB
  maxDimension = 2048,          // cap long edge
  mime = 'image/jpeg',
} = {}) {
  const bitmap = await fileToBitmap(file);
  let { width, height } = bitmap;

  // scale to fit long edge
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, width, height);
  if (bitmap.close) bitmap.close();

  // iteratively lower quality until under maxBytes
  let quality = 0.92;
  let dataUrl = canvas.toDataURL(mime, quality);
  let bytes = dataUrlBytes(dataUrl);
  while (bytes > maxBytes && quality > 0.35) {
    quality -= 0.1;
    dataUrl = canvas.toDataURL(mime, quality);
    bytes = dataUrlBytes(dataUrl);
  }

  // last resort: scale canvas down 20% and retry
  if (bytes > maxBytes) {
    const smaller = document.createElement('canvas');
    smaller.width = Math.round(width * 0.8);
    smaller.height = Math.round(height * 0.8);
    smaller.getContext('2d').drawImage(canvas, 0, 0, smaller.width, smaller.height);
    dataUrl = smaller.toDataURL(mime, 0.8);
    bytes = dataUrlBytes(dataUrl);
    width = smaller.width; height = smaller.height;
  }

  return { dataUrl, bytes, width, height, mime };
}

function dataUrlBytes(dataUrl) {
  // "data:mime;base64,<b64>"  →  bytes = b64.length * 3/4 - padding
  const b64 = dataUrl.split(',')[1] || '';
  const padding = (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
  return Math.floor((b64.length * 3) / 4) - padding;
}

async function fileToBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file); } catch { /* fall through */ }
  }
  // Safari / older browser fallback via <img>
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export function humanBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}
